const _figBrandColors: Record<string, Record<string, string>> = {
  'fig-sage': {
    50: '#F4F7F5',
    100: '#E5EEE8',
    200: '#CADCCD',
    300: '#A2BEA8',
    400: '#72997E', // FIG Green
    500: '#587D64',
    600: '#3F5E4C',
    700: '#2E4B3A',
    800: '#233B2D',
    900: '#172820',
    950: '#0C1410',
  },
  'fig-teal': {
    50: '#E9FAFB',
    100: '#D3EFF2',
    200: '#A9DCE3',
    300: '#7DC5CF',
    400: '#56AFBC',
    500: '#3596A5',
    600: '#1C7E8D',
    700: '#116678',
    800: '#017089', // brand color
    900: '#0A556B',
    950: '#013A4D',
  },
  'fig-orange': {
    50: '#FFF7F2',
    100: '#FFEADB',
    200: '#FFD1AE',
    300: '#F9AE74',
    400: '#EC7F35',
    500: '#D9611C',
    600: '#BF4C17',
    700: '#A84725', // brand color
    800: '#833216',
    900: '#5B2310',
    950: '#32140A',
  },
  'fig-red': {
    50: '#FDF6F6',
    100: '#FBE9E9',
    200: '#F4C5C5',
    300: '#E9989A',
    400: '#DA656A',
    500: '#C63A41',
    600: '#A92229',
    700: '#8E1318',
    800: '#811517', // FIG Red
    900: '#590707', // FIG Dark Red
    950: '#3A0406',
  },
  'fig-navy': {
    50: '#f6f9fb',
    100: '#ecf3f7',
    200: '#d9e7ef',
    300: '#b5d3e4',
    400: '#8ebdd4',
    500: '#689ebc', // FIG Light Blue
    600: '#386987',
    700: '#35597a',
    800: '#223F5E', // FIG Blue
    900: '#041A30', // FIG Navy
    950: '#020A18',
  },
  // 500–950 re-derived 2026-10-07 (OKLCH h 252, even L steps, chroma tapering into the darks). Revert: swap in 'fig-slate-legacy'.
  'fig-slate': {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#62758A',
    600: '#43566B',
    700: '#314255',
    800: '#1B2A3A',
    900: '#0D1926',
    950: '#030912',
  },
  // The surface ramp before the 2026-10-07 rescale. Not wired to any token — kept for revert.
  'fig-slate-legacy': {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#6C7D92',
    600: '#223F5E', // FIG Blue
    700: '#1A344A',
    800: '#0F243A',
    900: '#041A30', // FIG Navy
    950: '#020A18',
  },
  'fig-neutral': {
    50: '#FAFAFA',
    100: '#F2F2F2',
    200: '#E4E4E4', // FIG Gray
    300: '#CFCFCF',
    400: '#B3B3B3',
    500: '#8F8F8F',
    600: '#6C6C6C',
    700: '#4F4F4F',
    800: '#383838',
    900: '#1F1F1F',
    950: '#0D0D0D',
  },
  // Surface palette used by the `Black` preset for groups whose brand color is
  // black or near-black. Lighter shades (50-500) sit higher on the tonal scale
  // than a typical neutral ramp so light surfaces stay airy; the ramp accelerates
  // into pure black at 800-950 so dark surfaces read as jet-black, not muted
  // charcoal. Consumed by v1's `getPresetForExtremeHex` fallback path.
  'fig-jetblack': {
    50: '#FAFAFA',
    100: '#EFEFEF',
    200: '#D9D9D9',
    300: '#B8B8B8',
    400: '#8F8F8F',
    500: '#666666',
    600: '#484848',
    700: '#292929',
    800: '#131313',
    900: '#040404',
    950: '#000000',
  },
};

/**
 * Main color palette. This will power both TailwindCSS utility classes as well as PrimeNG's theme settings & design tokens
 * Color palette is copied from official TailwindCSS v3 palette
 */
const _colors: Record<string, Record<string, string>> = {
  slate: _figBrandColors['fig-slate'],
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5dc',
    400: '#99a1af',
    500: '#6a7282',
    600: '#4a5565',
    700: '#364153',
    800: '#1e2939',
    900: '#101828',
    950: '#030712'
  },
  zinc: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#9f9fa9',
    500: '#71717b',
    600: '#52525c',
    700: '#3f3f47',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b'
  },
  neutral: _figBrandColors['fig-neutral'],
  red: _figBrandColors['fig-red'],
  stone: {
    50: '#fafaf9',
    100: '#f5f5f4',
    200: '#e7e5e4',
    300: '#d6d3d1',
    400: '#a6a09b',
    500: '#79716b',
    600: '#57534d',
    700: '#44403b',
    800: '#292524',
    900: '#1c1917',
    950: '#0c0a09'
  },
  orange: _figBrandColors['fig-orange'],
  amber: {
    50: '#fffaef',  // was #fffbeb
    100: '#fef3d4',  // was #fef3c6
    200: '#fde79e',  // was #fee685
    300: '#fdd24f',  // was #ffd230
    400: '#f8b700',  // was #ffb900
    500: '#e99600',  // was #fe9a00
    600: '#c77400',  // was #e17100
    700: '#9e5200',  // was #bb4d00
    800: '#7d4100',  // was #973c00
    900: '#643400',  // was #7b3306
    950: '#371a00'   // was #461901
  },
  yellow: {
    50: '#fefce8',
    100: '#fef9c2',
    200: '#fff085',
    300: '#ffdf20',
    400: '#fdc700',
    500: '#f0b100',
    600: '#d08700',
    700: '#a65f00',
    800: '#894b00',
    900: '#733e0a',
    950: '#432004'
  },
  lime: {
    50: '#F6FAED',
    100: '#E8F3D2',
    200: '#CFE7A3',
    300: '#AACD66',
    400: '#87B43A',
    500: '#6D9728',
    600: '#587B1E',
    700: '#456016',
    800: '#374D12',
    900: '#2A3C0E',
    950: '#182207',
  },
  green: {
    50: '#F2FBF4',
    100: '#DFF6E3',
    200: '#BAE8C4',
    300: '#83D49C',
    400: '#4DBA73',
    500: '#2F9E58',
    600: '#26824A',
    700: '#1E653B',
    800: '#184F31',
    900: '#103A26',
    950: '#0A2317',
  },
  emerald: {
    50: '#F1FBF7',
    100: '#DBF4E9',
    200: '#B8E7D0',
    300: '#89D1AD',
    400: '#55B889',
    500: '#379E71',
    600: '#2B805C',
    700: '#23674B',
    800: '#1C523D',
    900: '#12362A',
    950: '#0B2119',
  },
  teal: {
    50: '#e9f9f9',
    100: '#c9eeee',
    200: '#9edee0',
    300: '#6dc9ce',
    400: '#45b3bb',
    500: '#359ea8',
    600: '#2f7e8b',
    700: '#2a6674',
    800: '#25535f',
    900: '#1f464f',
    950: '#132d35'
  },
  cyan: {
    50: '#daf8fc',     // was #ecfeff
    100: '#b8f4fb',    // was #cefafe
    200: '#8aeef9',    // was #a2f4fd
    300: '#38e1f8',    // was #53eafd
    400: '#00c4e6',    // was #00d3f3
    500: '#00a3c5',    // was #00b8db
    600: '#007ea3',    // was #0092b8
    700: '#006180',    // was #007595
    800: '#004d63',    // was #005f78
    900: '#0c3e52',    // was #104e64
    950: '#042938'     // was #053345
  },
  sky: {
    50: '#f0f9ff',
    100: '#dff2fe',
    200: '#b8e6fe',
    300: '#74d4ff',
    400: '#00bcff',
    500: '#00a6f4',
    600: '#0084d1',
    700: '#0069a8',
    800: '#00598a',
    900: '#024a70',
    950: '#052f4a'
  },
  blue: {
    50: '#f0f7ff',
    100: '#dfefff',
    200: '#b8e0ff',
    300: '#7ac7ff',
    400: '#33abfd',
    500: '#0890ef',
    600: '#0071cc',
    700: '#0059a5',
    800: '#044d88',
    900: '#094171',
    950: '#06284b',
  },
  indigo: {
    50: '#f8f9ff',  // was #eef2ff → brighter, near-white tint for dark BGs
    100: '#f0f3ff',  // was #e0e7ff → lighter, cleaner
    200: '#dbe2ff',  // was #c6d2ff → more airy, higher contrast
    300: '#b9c4ff',  // was #a3b3ff → softened but distinct
    400: '#8d99ff',  // was #7c86ff → higher clarity on both bg types
    500: '#6366ff',  // was #615fff → accessible indigo (kept strong)
    600: '#4f46e5',  // was #4f39f6 → tuned for readability, standard indigo
    700: '#4338ca',  // was #432dd7 → slightly lighter for WCAG contrast
    800: '#3730a3',  // was #372aac → balanced deep indigo
    900: '#312e81',  // was #312c85 → adjusted for better contrast
    950: '#1e1b4b'   // was #1e1a4d → kept nearly the same, strong anchor
  },
  violet: {
    50: '#F6F4FF',
    100: '#ECE9FE',
    200: '#D9D4FD',
    300: '#BAAEFB',
    400: '#9A80F2',
    500: '#825AE6',
    600: '#6E3BD2',
    700: '#5D26B5',
    800: '#4C1F91',
    900: '#3E1B74',
    950: '#27104C',
  },
  purple: {
    50: '#F7F4FF',
    100: '#EDE7FF',
    200: '#D8CDFE',
    300: '#BBA3FA',
    400: '#9A77F0',
    500: '#8153E0',
    600: '#6D35C6',
    700: '#5A27A6',
    800: '#492185',
    900: '#3A1B68',
    950: '#261148',
  },
  fuchsia: {
    50: '#FCF5FF',
    100: '#F8E9FE',
    200: '#F1CFFB',
    300: '#E6A7F3',
    400: '#D879E4',
    500: '#C752D0',
    600: '#AA32B2',
    700: '#8D2393',
    800: '#711E76',
    900: '#581A5B',
    950: '#3B0F3B',
  },
  pink: {
    50: '#faf0f6', // orig: #fdf2f8
    100: '#f8e1ee', // orig: #fce7f3
    200: '#f6c6e0', // orig: #fccee8
    300: '#f59ecb', // orig: #fda5d5
    400: '#ef6cae', // orig: #fb64b6
    500: '#e23d92', // orig: #f6339a
    600: '#cc1872', // orig: #e60076
    700: '#a90e59', // orig: #c6005c
    800: '#891247', // orig: #a3004c
    900: '#6f153e', // orig: #861043
    950: '#420b23', // orig: #510424
  },
  rose: {
    50: '#fff5f6', // orig: #fff1f2
    100: '#ffe9eb', // orig: #ffe4e6
    200: '#ffd6db', // orig: #ffccd3
    300: '#ffb3bb', // orig: #ffa1ad
    400: '#ff7a92', // orig: #ff637e
    500: '#ff456d', // orig: #ff2056
    600: '#f01a53', // orig: #ec003f
    700: '#d11448', // orig: #c70036
    800: '#af1746', // orig: #a50036
    900: '#941b42', // orig: #8b0836
    950: '#561026', // orig: #4d0218
  },
};

const _primitiveColors: Record<string, Record<string, string>> = {
  slate: _figBrandColors['fig-slate'],
  gray: _colors['gray'],
  zinc: _colors['zinc'],
  neutral: _figBrandColors['fig-neutral'],
  stone: _colors['stone'],
  red: _figBrandColors['fig-red'],
  orange: _figBrandColors['fig-orange'],
  amber: _colors['amber'],
  yellow: _colors['yellow'],
  lime: _colors['lime'],
  green: _colors['green'],
  emerald: _colors['emerald'],
  teal: _figBrandColors['fig-teal'],
  cyan: _colors['cyan'],
  sky: _colors['sky'],
  blue: _colors['blue'],
  indigo: _colors['indigo'],
  violet: _colors['violet'],
  purple: _colors['purple'],
  fuchsia: _colors['fuchsia'],
  pink: _colors['pink'],
  rose: _colors['rose'],
  sage: _figBrandColors['fig-sage'],
  navy: _figBrandColors['fig-navy'],
  jetblack: _figBrandColors['fig-jetblack'],
};

export const colors: Colors = {
  ..._primitiveColors,
  success: _colors['green'],
  info: _colors['blue'],
  warn: _colors['orange'],
  error: _colors['red'],
} as unknown as Colors;

// [retired, current] — user preferences store whole palettes, so a re-tuned stock ramp would otherwise stay stale forever.
const _retiredPalettes: [Record<string, string>, ColorShades][] = [
  [_figBrandColors['fig-slate-legacy'], colors.slate],
];

/** Resolves a saved copy of a retired stock palette to its current values; any other palette is returned as-is. */
export function migrateRetiredPalette(shades: ColorShades): ColorShades {
  const saved = shades as unknown as Record<string, string | undefined>;
  const match = _retiredPalettes.find(([retired]) =>
    Object.entries(retired).every(([stop, hex]) => saved[stop]?.toLowerCase() === hex.toLowerCase()));
  return match ? match[1] : shades;
}

export interface ColorShades {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export interface Colors {
  [key: string]: ColorShades;
  slate: ColorShades;
  gray: ColorShades;
  zinc: ColorShades;
  neutral: ColorShades;
  stone: ColorShades;
  red: ColorShades;
  orange: ColorShades;
  amber: ColorShades;
  yellow: ColorShades;
  lime: ColorShades;
  emerald: ColorShades;
  green: ColorShades;
  teal: ColorShades;
  cyan: ColorShades;
  sky: ColorShades;
  blue: ColorShades;
  indigo: ColorShades;
  violet: ColorShades;
  purple: ColorShades;
  fuchsia: ColorShades;
  pink: ColorShades;
  rose: ColorShades;
  sage: ColorShades;
  navy: ColorShades;
  jetblack: ColorShades;
  success: ColorShades;
  info: ColorShades;
  warn: ColorShades;
  error: ColorShades;
}

export interface ColorPalette {
  key: string,
  value: ColorShades,
}

export interface ThemePreset {
  name: string;
  primary: ColorShades;
  surface: ColorShades;
}